import { createClient } from '@supabase/supabase-js'
import { laToday } from './laTime'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Custom storage that falls back gracefully on mobile browsers
// that restrict localStorage (Safari ITP, private mode, etc.)
const customStorage = {
  getItem: (key) => {
    try { return localStorage.getItem(key) } catch {
      try { return sessionStorage.getItem(key) } catch { return null }
    }
  },
  setItem: (key, value) => {
    try { localStorage.setItem(key, value) } catch {
      try { sessionStorage.setItem(key, value) } catch {}
    }
  },
  removeItem: (key) => {
    try { localStorage.removeItem(key) } catch {}
    try { sessionStorage.removeItem(key) } catch {}
  },
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storage: customStorage,
  }
})

// ─── PORTAL USER MANAGEMENT (server-side) ────────────────────
// Account creation/deletion/password-reset run in the `manage-portal-user`
// Edge Function, which holds the service key server-side and enforces who may
// create what (HQ admins vs franchise admins, and only within their location).
// The service key is NEVER shipped to the browser.
async function callUserFn(body) {
  const { data, error } = await supabase.functions.invoke('manage-portal-user', { body })
  if (error) {
    // Try to surface the function's own error message when present.
    let msg = error.message
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error } catch {}
    throw new Error(msg || 'Request failed.')
  }
  if (!data?.ok) throw new Error(data?.error || 'Request failed.')
  return data
}

export function createPortalUser(payload) {
  return callUserFn({ action: 'create', ...payload })
}
export function deletePortalUser(userId) {
  return callUserFn({ action: 'delete', user_id: userId })
}
export function updatePortalUser(payload) {
  return callUserFn({ action: 'update', ...payload })
}
export async function listLoginPhones() {
  const { phones } = await callUserFn({ action: 'list_phones' })
  return phones || {}
}
export function resetPortalUserPassword(userId, password) {
  return callUserFn({ action: 'reset_password', user_id: userId, password })
}

// ─── AUTH ────────────────────────────────────────────────────
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

// Passwordless login: email the user a one-time code. shouldCreateUser:false
// means only accounts that already exist (created in User Management) can get a
// code — an unknown email gets nothing.
export async function sendLoginCode(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email: email.trim(),
    options: { shouldCreateUser: false },
  })
  if (error) throw error
}

// Verify the emailed code and establish a session.
export async function verifyLoginCode(email, token) {
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.trim(),
    token: token.trim(),
    type: 'email',
  })
  if (error) throw error
  return data
}

// Passwordless login by text (Quo). The sms-login edge function texts a code,
// then trades a correct code for a one-time token we exchange for a session.
async function callSmsLogin(body) {
  const { data, error } = await supabase.functions.invoke('sms-login', { body })
  if (error) {
    let msg = error.message
    try { const j = await error.context?.json?.(); if (j?.error) msg = j.error } catch {}
    throw new Error(msg || 'Request failed.')
  }
  return data
}

export async function sendSmsLoginCode(phone) {
  await callSmsLogin({ action: 'send', phone })
}

export async function verifySmsLoginCode(phone, code) {
  const { token_hash } = await callSmsLogin({ action: 'verify', phone, code })
  const { data, error } = await supabase.auth.verifyOtp({ token_hash, type: 'email' })
  if (error) throw error
  return data
}

export async function signOut() {
  await supabase.auth.signOut()
}

export async function getProfile() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data } = await supabase
    .from('user_profiles')
    .select('*, owner:owners(*), partner:partners(*), employee:employees(*), location:locations(*)')
    .eq('id', user.id)
    .single()
  return data
}

// ─── LOCATIONS (franchise multi-tenancy) ─────────────────────
export async function getLocations() {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .order('is_hq', { ascending: false })
    .order('name')
  if (error) throw error
  return data
}

// Full detail for one HQ car as seen by a franchise: sanitized specs, all
// photos, availability, the wholesale rate HQ set for them (read-only), and
// their own client-facing pricing (editable).
export async function getHqCar(carId) {
  const [car, photos, busy, wholesale, wholesaleTiers, retail, retailTiers, marketPricing, marketTiers] = await Promise.all([
    supabase.from('hq_fleet').select('*').eq('car_id', carId).single(),
    supabase.from('hq_fleet_photos').select('*').eq('car_id', carId),
    supabase.from('hq_fleet_availability').select('*').eq('car_id', carId).order('busy_from'),
    supabase.from('car_franchise_pricing').select('*').eq('car_id', carId).maybeSingle(),
    supabase.from('car_franchise_pricing_tiers').select('*').eq('car_id', carId).order('min_days'),
    supabase.from('franchise_car_retail_pricing').select('*').eq('car_id', carId).maybeSingle(),
    supabase.from('franchise_car_retail_pricing_tiers').select('*').eq('car_id', carId).order('min_days'),
    // Peacock's customer-facing market (retail) rate for this car — this is the
    // price the franchise sells at. Sanitized definer views (retail only).
    supabase.from('hq_fleet_market_pricing').select('*').eq('car_id', carId),
    supabase.from('hq_fleet_market_pricing_tiers').select('*').eq('car_id', carId).order('min_days'),
  ])
  if (car.error) throw car.error
  return {
    car: car.data,
    photos: photos.data || [],
    busy: busy.data || [],
    wholesale: wholesale.data || null,
    wholesaleTiers: wholesaleTiers.data || [],
    retail: retail.data || null,
    retailTiers: retailTiers.data || [],
    marketPricing: marketPricing.data || [],
    marketTiers: marketTiers.data || [],
  }
}

// Franchise saves its own client-facing pricing for an HQ car (replace in place).
export async function saveFranchiseRetail(carId, locationId, { use_tiers, daily_rate, tiers }) {
  await supabase.from('franchise_car_retail_pricing_tiers').delete().eq('car_id', carId).eq('location_id', locationId)
  await supabase.from('franchise_car_retail_pricing').delete().eq('car_id', carId).eq('location_id', locationId)
  const validTiers = use_tiers
    ? (tiers || []).filter(t => t.call_for_pricing || (t.daily_rate && parseFloat(t.daily_rate) > 0))
    : []
  const hasFlat = !use_tiers && daily_rate && parseFloat(daily_rate) > 0
  if (!hasFlat && validTiers.length === 0) return
  await supabase.from('franchise_car_retail_pricing').insert({
    car_id: carId, location_id: locationId,
    daily_rate: use_tiers ? null : parseFloat(daily_rate),
    use_tiers: !!use_tiers,
  })
  if (use_tiers && validTiers.length > 0) {
    await supabase.from('franchise_car_retail_pricing_tiers').insert(validTiers.map(t => ({
      car_id: carId, location_id: locationId, min_days: t.min_days, max_days: t.max_days, label: t.label,
      daily_rate: t.call_for_pricing ? null : parseFloat(t.daily_rate),
      call_for_pricing: t.call_for_pricing || false,
    })))
  }
}

// Shared retail (market) pricing write — lets the franchise car page edit the
// SAME PS rate HQ sets (2-way sync). Routes through the set_car_market_pricing
// RPC, which authorizes (a franchise may edit only its own market) and writes
// car_market_pricing + car_market_pricing_tiers. daily_rate is NOT NULL, so for
// tiered pricing we send the day-1 (base) rate as the parent rate.
export async function saveCarMarketPricing(carId, market, { use_tiers, daily_rate, tiers }) {
  const cleanTiers = use_tiers
    ? (tiers || []).map(t => ({
        min_days: t.min_days,
        max_days: t.max_days,
        label: t.label,
        daily_rate: t.call_for_pricing ? null : (t.daily_rate === '' || t.daily_rate == null ? null : parseFloat(t.daily_rate)),
        call_for_pricing: !!t.call_for_pricing,
        discount_pct: (t.discount_pct === '' || t.discount_pct == null) ? null : parseFloat(t.discount_pct),
      }))
    : []
  let base
  if (use_tiers) {
    const firstPriced = cleanTiers.find(t => !t.call_for_pricing && t.daily_rate != null)
    base = firstPriced ? firstPriced.daily_rate : 0
  } else {
    base = (daily_rate === '' || daily_rate == null) ? 0 : parseFloat(daily_rate)
  }
  const { error } = await supabase.rpc('set_car_market_pricing', {
    p_car_id: carId,
    p_market: market,
    p_use_tiers: !!use_tiers,
    p_daily_rate: base,
    p_tiers: cleanTiers,
  })
  if (error) throw error
}

// The one HQ location (used to tag HQ-side accounts).
export async function getHqLocation() {
  const { data, error } = await supabase
    .from('locations')
    .select('id, name')
    .eq('is_hq', true)
    .single()
  if (error) throw error
  return data
}

// Read-only window onto HQ's fleet for franchise users (sanitized views),
// plus the wholesale rate HQ set for THIS franchise (RLS returns only their own).
export async function getHqFleet() {
  const [{ data: cars }, { data: photos }, { data: busy }, { data: pricing }, { data: pricingTiers }, { data: marketPricing }, { data: marketTiers }] = await Promise.all([
    supabase.from('hq_fleet').select('*').order('make').order('model'),
    supabase.from('hq_fleet_photos').select('*'),
    supabase.from('hq_fleet_availability').select('*').order('busy_from'),
    supabase.from('car_franchise_pricing').select('*'),
    supabase.from('car_franchise_pricing_tiers').select('*').order('min_days'),
    // Peacock's customer-facing market (retail) rate — the franchise's sell price.
    supabase.from('hq_fleet_market_pricing').select('*'),
    supabase.from('hq_fleet_market_pricing_tiers').select('*').order('min_days'),
  ])
  return {
    cars: cars || [], photos: photos || [], busy: busy || [],
    pricing: pricing || [], pricingTiers: pricingTiers || [],
    marketPricing: marketPricing || [], marketTiers: marketTiers || [],
  }
}

// ─── OWNERS ──────────────────────────────────────────────────
export async function getOwners() {
  const { data, error } = await supabase
    .from('owners')
    .select('*')
    .order('name')
  if (error) throw error
  return data
}

export async function getOwner(id) {
  const { data, error } = await supabase
    .from('owners')
    .select('*, cars(*)')
    .eq('id', id)
    .single()
  if (error) throw error
  return data
}

export async function upsertOwner(owner) {
  const { data, error } = await supabase
    .from('owners')
    .upsert(owner)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteOwner(id) {
  const { error } = await supabase.from('owners').delete().eq('id', id)
  if (error) throw error
}

// ─── CARS ─────────────────────────────────────────────────────
export async function getCars(filters = {}) {
  let q = supabase
    .from('cars')
    .select('*, owner:owners(id, name, email)')
    .order('make')

  if (filters.ownership_type) q = q.eq('ownership_type', filters.ownership_type)
  if (filters.status) q = q.eq('status', filters.status)
  if (filters.owner_id) q = q.eq('owner_id', filters.owner_id)

  const { data, error } = await q
  if (error) throw error
  return data
}

export async function getCar(id) {
  const { data, error } = await supabase
    .from('cars')
    .select('*, owner:owners(*), partner:partners(*)')
    .eq('id', id)
    .single()
  if (error) throw error
  return data
}

export async function upsertCar(car) {
  const { data, error } = await supabase
    .from('cars')
    .upsert(car)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteCar(id) {
  const { error } = await supabase.from('cars').delete().eq('id', id)
  if (error) throw error
}

// Archive (soft-delete): hides the car from the fleet grid but keeps the car
// row + all rental history intact. Used when a car has rental records and so
// cannot be permanently deleted. Reversible via unarchiveCar().
export async function archiveCar(id) {
  const { data: { user } } = await supabase.auth.getUser()
  const { error } = await supabase
    .from('cars')
    .update({ archived_at: new Date().toISOString(), archived_by: user?.id || null })
    .eq('id', id)
  if (error) throw error
}

// Restore an archived car back into the active fleet.
export async function unarchiveCar(id) {
  const { error } = await supabase
    .from('cars')
    .update({ archived_at: null, archived_by: null })
    .eq('id', id)
  if (error) throw error
}

// ─── RENTALS ──────────────────────────────────────────────────
export async function getRentals(filters = {}) {
  let q = supabase
    .from('rentals')
    .select('*, car:cars(id, make, model, year, color, ownership_type, owner_id, owner:owners(id,name)), renter:renters(*)')
    .order('start_date', { ascending: false })

  if (filters.status) q = q.eq('status', filters.status)
  if (filters.car_id) q = q.eq('car_id', filters.car_id)
  if (filters.location_id) q = q.eq('location_id', filters.location_id)
  if (filters.payout_status) q = q.eq('payout_status', filters.payout_status)
  if (filters.month && filters.year) {
    const start = `${filters.year}-${String(filters.month).padStart(2,'0')}-01`
    const end = new Date(filters.year, filters.month, 0)
    q = q.gte('start_date', start).lte('start_date', end.toISOString().split('T')[0])
  }

  const { data, error } = await q
  if (error) throw error
  return data
}

export async function getRental(id) {
  const { data, error } = await supabase
    .from('rentals')
    .select('*, car:cars(*, owner:owners(*)), renter:renters(*)')
    .eq('id', id)
    .single()
  if (error) throw error
  return data
}

export async function upsertRental(rental) {
  const { data, error } = await supabase
    .from('rentals')
    .upsert({ ...rental, updated_at: new Date().toISOString() })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function calculatePayout(rentalId) {
  const { error } = await supabase.rpc('calculate_rental_payout', { rental_id: rentalId })
  if (error) throw error
}

export async function markRentalPaid(rentalId) {
  const { error } = await supabase
    .from('rentals')
    .update({ payout_status: 'paid', payout_date: laToday() })
    .eq('id', rentalId)
  if (error) throw error
}

// ─── OWNER DASHBOARD DATA ─────────────────────────────────────
export async function getOwnerDashboard(ownerId) {
  // Get all their cars
  const { data: cars, error: carsErr } = await supabase
    .from('cars')
    .select('*')
    .eq('owner_id', ownerId)
  if (carsErr) throw carsErr

  const carIds = cars.map(c => c.id)
  if (carIds.length === 0) return { cars: [], rentals: [], stats: {} }

  // Get all rentals for those cars
  const { data: rentals, error: rentErr } = await supabase
    .from('rentals')
    .select('*, car:cars(make, model, year, color)')
    .in('car_id', carIds)
    .order('start_date', { ascending: false })
  if (rentErr) throw rentErr

  // Compute stats
  const now = new Date()
  const thisMonth = rentals.filter(r => {
    const d = new Date(r.start_date)
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  })

  const stats = {
    total_earned_all_time: rentals.reduce((s, r) => s + (r.owner_base_payout || 0) + (r.owner_overage_payout || 0) + (r.broker_cost || 0), 0),
    total_earned_this_month: thisMonth.reduce((s, r) => s + (r.owner_base_payout || 0) + (r.owner_overage_payout || 0) + (r.broker_cost || 0), 0),
    pending_payout: rentals.filter(r => r.payout_status === 'pending' && r.status === 'completed')
      .reduce((s, r) => s + (r.owner_base_payout || 0) + (r.owner_overage_payout || 0) + (r.broker_cost || 0), 0),
    total_rentals: rentals.length,
    active_rentals: rentals.filter(r => r.status === 'active').length,
  }

  return { cars, rentals, stats }
}

// ─── ADMIN STATS ──────────────────────────────────────────────
export async function getAdminStats() {
  const [
    { count: totalCars },
    { count: activeCars },
    { data: recentRentals },
    { data: pendingPayouts }
  ] = await Promise.all([
    supabase.from('cars').select('*', { count: 'exact', head: true }),
    supabase.from('cars').select('*', { count: 'exact', head: true }).eq('status', 'rented'),
    supabase.from('rentals')
      .select('*, car:cars(make,model,year)')
      .order('created_at', { ascending: false })
      .limit(5),
    supabase.from('rentals')
.select('*, car:cars(make,model,year,ownership_type,owner_id,partner_id, owner:owners(name), partner:partners(company_name))')
      .eq('payout_status', 'pending')
      .eq('status', 'completed')
  ])

  const totalPendingPayout = (pendingPayouts || []).reduce(
    (s, r) => s + (r.owner_base_payout || 0) + (r.owner_overage_payout || 0) + (r.broker_cost || 0), 0
  )

  // This month revenue
  const now = new Date()
  const { data: thisMonthRentals } = await supabase
    .from('rentals')
    .select('total_charged, your_profit')
    .gte('start_date', `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`)

  const monthRevenue = (thisMonthRentals || []).reduce((s, r) => s + (r.total_charged || 0), 0)
  const monthProfit = (thisMonthRentals || []).reduce((s, r) => s + (r.your_profit || 0), 0)

  return {
    totalCars: totalCars || 0,
    activeCars: activeCars || 0,
    recentRentals: recentRentals || [],
    pendingPayouts: pendingPayouts || [],
    totalPendingPayout,
    monthRevenue,
    monthProfit,
  }
}

// ─── RENTERS ──────────────────────────────────────────────────
export async function upsertRenter(renter) {
  const { data, error } = await supabase
    .from('renters')
    .upsert(renter)
    .select()
    .single()
  if (error) throw error
  return data
}

// ─── BOOQABLE SYNC ────────────────────────────────────────────
export async function syncFromBooqable(booqableApiKey) {
  // Booqable API base URL
  const base = 'https://api.booqable.com/api/1'
  const headers = { 'Authorization': `Bearer ${booqableApiKey}` }

  // Fetch orders from Booqable
  const res = await fetch(`${base}/orders?filter[status]=started,stopped&include=lines,customer`, { headers })
  if (!res.ok) throw new Error(`Booqable API error: ${res.status}`)
  const { data: orders, included } = await res.json()

  let synced = 0
  for (const order of orders) {
    // Try to match car by product name or VIN stored in Booqable tags
    const customerId = order.relationships?.customer?.data?.id
    const customer = included?.find(i => i.type === 'customers' && i.id === customerId)

    // Check if this order is already in our DB
    const { data: existing } = await supabase
      .from('rentals')
      .select('id')
      .eq('booqable_order_id', order.id)
      .single()

    if (!existing) {
      // We insert a skeleton rental — admin matches the car manually
      await supabase.from('rentals').insert({
        booqable_order_id: order.id,
        renter_name: customer?.attributes?.name || 'Unknown',
        start_date: order.attributes?.starts_at?.split('T')[0],
        end_date: order.attributes?.stops_at?.split('T')[0],
        days: 1, // Admin fills in properly
        daily_rate: 0,
        base_total: order.attributes?.price_in_cents / 100 || 0,
        total_charged: order.attributes?.price_in_cents / 100 || 0,
        status: order.attributes?.status === 'started' ? 'active' : 'completed',
        notes: `Synced from Booqable order ${order.id}`,
      })
      synced++
    }
  }
  return synced
}
